/**
 * Error Report Generator Utility
 * Generates downloadable CSV reports for validation errors and provisioning errors
 */
/**
 * Escape a CSV value (wrap in quotes if contains comma, newline, or quotes)
 */
function escapeCSVValue(value) {
    if (value === null || value === undefined) {
        return "";
    }
    const str = String(value);
    if (str.includes(",") || str.includes("\n") || str.includes('"') || str.includes("\r")) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
}
/**
 * Generate a CSV row from values
 */
function generateCSVRow(values) {
    return values.map(escapeCSVValue).join(",");
}
/**
 * Generate a timestamped filename
 */
function generateTimestamp() {
    return new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
}
/**
 * Trigger a file download
 */
function triggerDownload(content, filename, mimeType = "text/csv;charset=utf-8;") {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}
/**
 * Generate and download a validation errors report
 */
export function downloadValidationErrorsReport(errors, records) {
    const rows = [];
    // Header row
    rows.push(generateCSVRow([
        "Row Number",
        "First Name",
        "Last Name",
        "Email",
        "Field Name",
        "Error Message",
    ]));
    // Group errors by row for context
    const errorsByRow = errors.reduce((acc, error) => {
        if (!acc[error.rowNumber])
            acc[error.rowNumber] = [];
        acc[error.rowNumber].push(error);
        return acc;
    }, {});
    // Add data rows
    for (const [rowNum, rowErrors] of Object.entries(errorsByRow)) {
        const record = records?.find((r) => r.rowNumber === Number(rowNum));
        for (const error of rowErrors) {
            rows.push(generateCSVRow([
                rowNum,
                record?.firstName || "",
                record?.lastName || "",
                record?.email || "",
                error.fieldName,
                error.errorMessage,
            ]));
        }
    }
    const filename = `ValidationErrors_${generateTimestamp()}.csv`;
    triggerDownload(rows.join("\n"), filename);
    return filename;
}
/**
 * Generate and download a provisioning errors/warnings report
 */
export function downloadProvisioningErrorsReport(createdUsers) {
    const rows = [];
    // Header row
    rows.push(generateCSVRow([
        "Practitioner Name",
        "User ID",
        "Status",
        "Message Type",
        "Message",
        "Timestamp",
    ]));
    // Add data rows for users with messages
    for (const user of createdUsers) {
        if (user.messages && user.messages.length > 0) {
            for (const msg of user.messages) {
                rows.push(generateCSVRow([
                    user.displayName,
                    user.userId,
                    user.status || "",
                    msg.type,
                    msg.message.replace(/\r\n/g, " | "), // Replace line breaks for CSV readability
                    msg.createdUtc,
                ]));
            }
        }
        else if (user.status === "Warn" || user.status === "Error") {
            // User has warning/error status but no detailed messages
            rows.push(generateCSVRow([
                user.displayName,
                user.userId,
                user.status,
                user.status,
                `User ${user.status === "Warn" ? "warning" : "error"} - no details available`,
                "",
            ]));
        }
    }
    const filename = `ProvisioningErrors_${generateTimestamp()}.csv`;
    triggerDownload(rows.join("\n"), filename);
    return filename;
}
/**
 * Generate and download a full provisioning results report
 * Includes all practitioners with their status and any messages
 */
export function downloadProvisioningResultsReport(createdUsers, jobId) {
    const rows = [];
    // Header row
    rows.push(generateCSVRow([
        "Practitioner Name",
        "User ID",
        "Identity ID",
        "Status",
        "Messages",
    ]));
    // Add data rows for all users
    for (const user of createdUsers) {
        const messagesText = user.messages
            ?.map((m) => `[${m.type}] ${m.message.replace(/\r\n/g, " | ")}`)
            .join(" || ") || "";
        rows.push(generateCSVRow([
            user.displayName,
            user.userId,
            user.identityId,
            user.status || "Unknown",
            messagesText,
        ]));
    }
    const jobSuffix = jobId ? `_${jobId.slice(0, 8)}` : "";
    const filename = `ProvisioningResults${jobSuffix}_${generateTimestamp()}.csv`;
    triggerDownload(rows.join("\n"), filename);
    return filename;
}
/**
 * Check if there are any errors to download
 */
export function hasValidationErrors(errors) {
    return errors.length > 0;
}
/**
 * Check if there are any provisioning errors/warnings to download
 */
export function hasProvisioningErrors(createdUsers) {
    return createdUsers.some((u) => u.status === "Warn" ||
        u.status === "Error" ||
        (u.messages && u.messages.length > 0));
}
